import React, { useMemo } from 'react';
import styles from './PracticeList.module.css';
import type { PracticeRecord } from './TrainingDashboard';

export interface PracticeListProps {
  practices: PracticeRecord[];
  showInactive?: boolean;
}

const CATEGORY_ORDER = ['pricing', 'scope', 'product', 'installation', 'accounting'];

const DOT_CLASS: Record<string, string> = {
  pricing: styles.dotPricing,
  scope: styles.dotScope,
  product: styles.dotProduct,
  installation: styles.dotInstallation,
  accounting: styles.dotAccounting,
};

function priorityClass(priority: string): string {
  switch (priority) {
    case 'critical':
      return styles.priorityCritical;
    case 'standard':
      return styles.priorityStandard;
    case 'nice-to-have':
      return styles.priorityNice;
    default:
      return styles.priorityStandard;
  }
}

function confidenceLevel(confidence: number): string {
  if (confidence >= 0.8) return styles.confidenceHigh;
  if (confidence >= 0.5) return styles.confidenceMedium;
  return styles.confidenceLow;
}

export const PracticeList: React.FC<PracticeListProps> = ({
  practices,
  showInactive = false,
}) => {
  const filtered = useMemo(() => {
    return showInactive ? practices : practices.filter((p) => p.active);
  }, [practices, showInactive]);

  const grouped = useMemo(() => {
    const groups: Record<string, PracticeRecord[]> = {};
    filtered.forEach((p) => {
      const cat = p.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    });
    return groups;
  }, [filtered]);

  const sortedCategories = useMemo(() => {
    return Object.keys(grouped).sort((a, b) => {
      const idxA = CATEGORY_ORDER.indexOf(a);
      const idxB = CATEGORY_ORDER.indexOf(b);
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    });
  }, [grouped]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Active Practices</h3>
        <span className={styles.count}>{filtered.length} practices</span>
      </div>

      <div className={styles.list}>
        {sortedCategories.length === 0 && (
          <div className={styles.emptyState}>No practices recorded yet</div>
        )}

        {sortedCategories.map((category) => (
          <div className={styles.categoryGroup} key={category}>
            <div className={styles.categoryHeader}>
              <span className={`${styles.categoryDot} ${DOT_CLASS[category] || styles.dotPricing}`} />
              <span className={styles.categoryLabel}>{category}</span>
              <span className={styles.categoryCount}>{grouped[category].length}</span>
            </div>

            {grouped[category].map((practice) => (
              <div className={styles.practiceItem} key={practice.id}>
                <div className={styles.practiceTitle}>{practice.title}</div>
                <div className={styles.practiceDescription}>{practice.description}</div>

                <div className={styles.practiceMeta}>
                  <span className={`${styles.metaTag} ${priorityClass(practice.priority)}`}>
                    {practice.priority}
                  </span>
                  {practice.subcategory && (
                    <span className={`${styles.metaTag} ${styles.priorityNice}`}>
                      {practice.subcategory}
                    </span>
                  )}
                </div>

                <div className={styles.practiceStats}>
                  <span className={styles.taughtBy}>
                    Taught by{' '}
                    <span className={styles.taughtByName}>{practice.taughtBy}</span>
                  </span>

                  <div className={styles.stat}>
                    Confidence:
                    <div className={styles.confidenceBar}>
                      <div
                        className={`${styles.confidenceFill} ${confidenceLevel(practice.confidence)}`}
                        style={{ width: `${practice.confidence * 100}%` }}
                      />
                    </div>
                    <span className={styles.statValue}>
                      {Math.round(practice.confidence * 100)}%
                    </span>
                  </div>

                  <div className={styles.stat}>
                    Applied: <span className={styles.statValue}>{practice.timesApplied}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
